// Press-time yield preview: bucket harvest points onto 8 m cells in the SAME turn as identification
// no stack download, no fire-and-forget. Arithmetic mean per occupied cell, then the SAME
// display-only percentile clip the final machine-yield layer uses.
import { useViewedCovariates } from "../stores/viewedCovariates";
import { useYieldPreviewStore } from "../stores/yieldPreviewStore";
import { useLayerStore } from "../stores/layerStore";
import { useUiStore } from "../stores/uiStore";
import { defaultUiState } from "./layerRegistry";
import type { ManifestLayer } from "../types/contract";
import type { YieldPoint } from "./yieldParse";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

/* * compute's `_winsor` on machine-yield layers (`productivity.py`) — 2nd/98th of finite cells. */
export const YIELD_DISPLAY_CLIP_PCTS = [2, 98] as const;
export const YIELD_PREVIEW_CELL_M = 8;
export const YIELD_PREVIEW_LAYER_ID = "yield-preview";

export function percentileLinear(sortedAsc: number[], p: number): number {
  if (!sortedAsc.length) return NaN;
  const idx = (p / 100) * (sortedAsc.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo]!;
  const t = idx - lo;
  return sortedAsc[lo]! * (1 - t) + sortedAsc[hi]! * t;
}

/* * Clip cell values to the 2nd–98th percentile. Does not mutate the input. */
export function clipYieldDisplay(values: (number | null)[]): {
  clipped: (number | null)[];
  lo: number | null;
  hi: number | null;
} {
  const finite = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  if (!finite.length) return { clipped: values, lo: null, hi: null };
  if (finite.length === 1)
    return { clipped: values, lo: finite[0]!, hi: finite[0]! };
  const sorted = [...finite].sort((a, b) => a - b);
  const lo = percentileLinear(sorted, YIELD_DISPLAY_CLIP_PCTS[0]);
  const hi = percentileLinear(sorted, YIELD_DISPLAY_CLIP_PCTS[1]);
  return {
    clipped: values.map((v) =>
      v == null || !Number.isFinite(v) ? v : Math.min(hi, Math.max(lo, v)),
    ),
    lo,
    hi,
  };
}

/*
 *
 * Per-cell arithmetic mean of positive finite yield values. Assignment is nearest
 * centroid in an 8 m hash — the stack cells ARE 8 m, so a point belongs to the cell
 * whose centre it is closest to (no swath weighting; decision 3).
 */
export function meanYieldByCell(
  points: { lon: number; lat: number; value: number }[],
  centroids: { lng: number; lat: number }[],
): (number | null)[] {
  const n = centroids.length;
  const out: (number | null)[] = Array.from({ length: n }, () => null);
  if (!n || !points.length) return out;
  const refLat = centroids.reduce((s, c) => s + c.lat, 0) / n;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((refLat * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const cell = YIELD_PREVIEW_CELL_M;
  const q = (lng: number, lat: number) =>
    `${Math.round((lng * kx) / cell)}:${Math.round((lat * ky) / cell)}`;
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < n; i++) {
    const k = q(centroids[i]!.lng, centroids[i]!.lat);
    const list = buckets.get(k);
    if (list) list.push(i);
    else buckets.set(k, [i]);
  }
  const sums = new Float64Array(n);
  const counts = new Uint32Array(n);
  for (const p of points) {
    if (!Number.isFinite(p.value) || p.value <= 0) continue;
    const cx = Math.round((p.lon * kx) / cell);
    const cy = Math.round((p.lat * ky) / cell);
    let best = -1;
    let bestD = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const i of buckets.get(`${cx + dx}:${cy + dy}`) ?? []) {
          const dlon = (centroids[i]!.lng - p.lon) * kx;
          const dlat = (centroids[i]!.lat - p.lat) * ky;
          const d = dlon * dlon + dlat * dlat;
          if (d < bestD) {
            bestD = d;
            best = i;
          }
        }
      }
    }
    if (best >= 0) {
      sums[best] += p.value;
      counts[best]++;
    }
  }
  for (let i = 0; i < n; i++) if (counts[i]) out[i] = sums[i]! / counts[i]!;
  return out;
}

export function buildYieldPreviewLayer(
  geoms: { type: string; coordinates: unknown }[],
  centroids: { lng: number; lat: number }[],
  points: YieldPoint[],
  opts: { title?: string; contentVersion?: string } = {},
): ManifestLayer {
  const samples = harvestSamples(points);
  const means = meanYieldByCell(samples, centroids);
  const { clipped, lo, hi } = clipYieldDisplay(means);
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const v = clipped[i];
    if (v == null || !Number.isFinite(v)) continue;
    features.push({
      type: "Feature",
      properties: { value: v },
      geometry: geoms[i],
    });
  }
  const vmin = lo ?? 0;
  const vmax = hi != null && hi > vmin ? hi : vmin + 1;
  return {
    id: YIELD_PREVIEW_LAYER_ID,
    kind: "geojson",
    title: opts.title ?? "Yield (visible, not yet usable)",
    inline: { type: "FeatureCollection", features },
    unit: "bu/ac",
    colormap: "viridis",
    value_min: vmin,
    value_max: vmax,
    default_visible: true,
    default_opacity: 0.85,
    order: 54,
    content_version: opts.contentVersion ?? "preview",
    is_stale: false,
  };
}

/*
 *
 * Occupied 8 m cells from the harvest itself. Press-time and CPU-only: a stack parquet
 * fetch would make Visualize wait on a background download. Cell centres follow the same
 * metric hash `meanYieldByCell` uses; empty cells are never emitted.
 */
export function occupiedYieldCells(
  points: { lon: number; lat: number; value: number }[],
  cellM = YIELD_PREVIEW_CELL_M,
): { geoms: { type: string; coordinates: unknown }[]; means: number[] } {
  if (!points.length) return { geoms: [], means: [] };
  const refLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((refLat * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const buckets = new Map<
    string,
    { sum: number; n: number; cx: number; cy: number }
  >();
  for (const p of points) {
    if (!Number.isFinite(p.value) || p.value <= 0) continue;
    const cx = Math.round((p.lon * kx) / cellM);
    const cy = Math.round((p.lat * ky) / cellM);
    const k = `${cx}:${cy}`;
    const b = buckets.get(k);
    if (b) {
      b.sum += p.value;
      b.n++;
    } else buckets.set(k, { sum: p.value, n: 1, cx, cy });
  }
  const geoms: { type: string; coordinates: unknown }[] = [];
  const means: number[] = [];
  const dLng = cellM / (kx * 2);
  const dLat = cellM / (ky * 2);
  for (const b of buckets.values()) {
    const lng = (b.cx * cellM) / kx;
    const lat = (b.cy * cellM) / ky;
    geoms.push({
      type: "Polygon",
      coordinates: [
        [
          [lng - dLng, lat - dLat],
          [lng + dLng, lat - dLat],
          [lng + dLng, lat + dLat],
          [lng - dLng, lat + dLat],
          [lng - dLng, lat - dLat],
        ],
      ],
    });
    means.push(b.sum / b.n);
  }
  return { geoms, means };
}

function harvestSamples(
  points: YieldPoint[],
): { lon: number; lat: number; value: number }[] {
  const out: { lon: number; lat: number; value: number }[] = [];
  for (const p of points) {
    const v = [p.ch.VRYIELDVOL, p.ch.AppliedRate, p.ch.ApplDepth].find(
      (x) => typeof x === "number" && Number.isFinite(x) && x > 0,
    );
    if (typeof v === "number") out.push({ lon: p.lon, lat: p.lat, value: v });
  }
  return out;
}

export function layerFromOccupiedCells(
  geoms: { type: string; coordinates: unknown }[],
  means: number[],
  opts: { id?: string; title?: string; contentVersion?: string } = {},
): ManifestLayer {
  const { clipped, lo, hi } = clipYieldDisplay(means);
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const v = clipped[i];
    if (v == null || !Number.isFinite(v)) continue;
    features.push({
      type: "Feature",
      properties: { value: v },
      geometry: geoms[i],
    });
  }
  const vmin = lo ?? 0;
  const vmax = hi != null && hi > vmin ? hi : vmin + 1;
  return {
    id: opts.id ?? YIELD_PREVIEW_LAYER_ID,
    kind: "geojson",
    title: opts.title ?? "Yield (visible, not yet usable)",
    inline: { type: "FeatureCollection", features },
    unit: "bu/ac",
    colormap: "viridis",
    value_min: vmin,
    value_max: vmax,
    default_visible: true,
    default_opacity: 0.85,
    order: 54,
    content_version: opts.contentVersion ?? "preview",
    is_stale: false,
  };
}

function slug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "x"
  );
}

export function yieldPreviewLayerId(
  fieldId: string,
  crop: string | null,
  year: string | null,
): string {
  return `yield-preview-${slug(fieldId)}-${slug(crop ?? "crop")}-${year ?? "year"}`;
}

export function yieldPreviewTitle(
  crop: string | null,
  year: string | null,
): string {
  const head = [crop, year].filter(Boolean).join(" ");
  return `${head ? `${head} ` : ""}yield (visible, not yet usable)`;
}

function cropTokens(crop: string): string[] {
  const t = crop.toLowerCase().trim();
  if (/corn|maize/.test(t)) return ["corn", "maize"];
  if (/soy/.test(t)) return ["soy"];
  if (/wheat/.test(t)) return ["wheat"];
  if (/canola/.test(t)) return ["canola"];
  if (/sorghum/.test(t)) return ["sorghum"];
  return t ? [t] : [];
}

/*
 * True when a catalog machine column has landed that covers this preview (crop + year).
 * Harvest keys (`mach_yield*` / `mach_yc`) plus seeding/application/tillage (`mach_seedrate*` /
 * `mach_appl*` / `mach_till*`) — compute's attach `columns` for non-harvest. A year-less
 * `Yield (Corn)` row must not retire a 2024 preview — that field already had corn.
 */
export function catalogCoversPreview(
  covs: {
    key: string;
    title?: string;
    category: string;
    has_values: boolean;
  }[],
  crop: string | null,
  year: string | null,
): boolean {
  const rows = covs.filter((c) => c.category === "machine" && c.has_values);
  const hayOf = (c: (typeof rows)[0]) =>
    `${c.key} ${c.title ?? ""}`.toLowerCase();
  const machineish = (h: string) =>
    /yield|\bmach_y[cs]\b|mach_seedrate|mach_appl|mach_till/.test(h);
  let pool = rows.filter((c) => machineish(hayOf(c)));
  if (year) pool = pool.filter((c) => hayOf(c).includes(year));
  if (!pool.length) return false;
  if (!crop) return !!year;
  const tokens = cropTokens(crop);
  return pool.some((c) => tokens.some((t) => hayOf(c).includes(t)));
}

function revealPreview(
  fieldId: string,
  layer: ManifestLayer,
  meta: { crop: string | null; year: string | null },
): void {
  useYieldPreviewStore.getState().put(fieldId, {
    layer,
    crop: meta.crop,
    year: meta.year,
    autoRevealed: true,
  });
  const viewed = useViewedCovariates.getState();
  for (const l of [...(viewed.byField[fieldId] ?? [])])
    viewed.remove(fieldId, l.id);
  viewed.add(fieldId, layer);
  const ui = useUiStore.getState();
  if (ui.mapOpen && ui.selectedFieldId === fieldId) {
    const store = useLayerStore.getState();
    if (!store.byId[layer.id]) {
      useLayerStore.setState({
        byId: { ...store.byId, [layer.id]: defaultUiState(layer) },
      });
    }
    useLayerStore.getState().setVisible(layer.id, true);
  }
}

/*
 *
 * Bucket this harvest onto 8 m occupied cells and park it on Visualize in THIS turn.
 * Does not fetch the stack and does not return a Promise.
 */
export function installYieldPreview(
  fieldId: string,
  points: YieldPoint[],
  meta: { crop: string | null; date: string | null },
): ManifestLayer | null {
  const samples = harvestSamples(points);
  if (!samples.length) return null;
  const { geoms, means } = occupiedYieldCells(samples);
  if (!geoms.length) return null;
  const year = meta.date?.slice(0, 4) ?? null;
  const layer = layerFromOccupiedCells(geoms, means, {
    id: yieldPreviewLayerId(fieldId, meta.crop, year),
    title: yieldPreviewTitle(meta.crop, year),
  });
  const n =
    (layer.inline as { features?: unknown[] } | undefined)?.features?.length ??
    0;
  if (!n) return null;
  revealPreview(fieldId, layer, { crop: meta.crop, year });
  return layer;
}

export function showYieldAttestationPreviews(
  attested: {
    envelope: {
      operations: {
        crop: string | null;
        opDate: string | null;
        fields: { fieldId: string }[];
      }[];
    };
    previewOps?: { points: YieldPoint[] }[];
  }[],
): void {
  for (const a of attested) {
    for (let i = 0; i < a.envelope.operations.length; i++) {
      const op = a.envelope.operations[i]!;
      const points = a.previewOps?.[i]?.points ?? [];
      for (const f of op.fields) {
        installYieldPreview(f.fieldId, points, {
          crop: op.crop,
          date: op.opDate,
        });
      }
    }
  }
}

/* * Drop every press-time stand-in on this field (viewed layer too). */
export function retireYieldPreviewField(fieldId: string): void {
  const store = useYieldPreviewStore.getState();
  for (const e of [...(store.byField[fieldId] ?? [])]) {
    store.drop(fieldId, e.layer.id);
    useViewedCovariates.getState().remove(fieldId, e.layer.id);
  }
}
